<template>
  <section class="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
    <h2 class="text-lg font-bold text-[#0B2447]">Sécurité</h2>
    <p class="text-gray-500 text-sm mt-1">
      Modifiez votre mot de passe pour protéger l'accès à votre compte.
    </p>

    <form class="mt-8 space-y-5 max-w-md" novalidate @submit.prevent="submit">
      <!-- Mot de passe actuel -->
      <div>
        <label for="pwd-current" class="block text-sm font-medium text-[#0B2447] mb-1.5">
          Mot de passe actuel
        </label>
        <input id="pwd-current" v-model="form.current" :type="visible ? 'text' : 'password'"
          autocomplete="current-password" :aria-invalid="errors.current ? 'true' : 'false'"
          :aria-describedby="errors.current ? 'pwd-current-error' : null"
          class="w-full rounded-xl border px-4 py-2.5 text-sm text-[#0B2447] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2447]/30 focus:border-[#0B2447]"
          :class="errors.current ? 'border-red-300' : 'border-gray-200'" @input="errors.current = null" />
        <p v-if="errors.current" id="pwd-current-error" class="mt-1.5 text-xs text-red-600">
          {{ errors.current }}
        </p>
      </div>

      <!-- Nouveau mot de passe -->
      <div>
        <label for="pwd-new" class="block text-sm font-medium text-[#0B2447] mb-1.5">
          Nouveau mot de passe
        </label>
        <input id="pwd-new" v-model="form.next" :type="visible ? 'text' : 'password'" autocomplete="new-password"
          :aria-invalid="errors.next ? 'true' : 'false'"
          :aria-describedby="'pwd-rules' + (errors.next ? ' pwd-new-error' : '')"
          class="w-full rounded-xl border px-4 py-2.5 text-sm text-[#0B2447] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2447]/30 focus:border-[#0B2447]"
          :class="errors.next ? 'border-red-300' : 'border-gray-200'" @input="errors.next = null" />
        <p v-if="errors.next" id="pwd-new-error" class="mt-1.5 text-xs text-red-600">
          {{ errors.next }}
        </p>

        <!-- Jauge de robustesse -->
        <div v-if="form.next" class="mt-3">
          <div class="flex gap-1.5" aria-hidden="true">
            <span v-for="i in 4" :key="i" class="h-1.5 flex-1 rounded-full transition-colors"
              :class="i <= score ? strength.bar : 'bg-gray-100'" />
          </div>
          <p class="mt-1.5 text-xs font-medium" :class="strength.text">
            Robustesse : {{ strength.label }}
          </p>
        </div>

        <ul id="pwd-rules" class="mt-3 space-y-1">
          <li v-for="rule in rules" :key="rule.label" class="flex items-center gap-2 text-xs"
            :class="rule.ok ? 'text-emerald-700' : 'text-gray-400'">
            <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path v-if="rule.ok" d="M4 10.5l4 4 8-9" />
              <circle v-else cx="10" cy="10" r="6" />
            </svg>
            <span>
              {{ rule.label }}
              <span class="sr-only">{{ rule.ok ? ' : respecté' : ' : non respecté' }}</span>
            </span>
          </li>
        </ul>
      </div>

      <!-- Confirmation -->
      <div>
        <label for="pwd-confirm" class="block text-sm font-medium text-[#0B2447] mb-1.5">
          Confirmer le nouveau mot de passe
        </label>
        <input id="pwd-confirm" v-model="form.confirm" :type="visible ? 'text' : 'password'" autocomplete="new-password"
          :aria-invalid="errors.confirm ? 'true' : 'false'"
          :aria-describedby="errors.confirm ? 'pwd-confirm-error' : null"
          class="w-full rounded-xl border px-4 py-2.5 text-sm text-[#0B2447] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2447]/30 focus:border-[#0B2447]"
          :class="errors.confirm ? 'border-red-300' : 'border-gray-200'" @input="errors.confirm = null" />
        <p v-if="errors.confirm" id="pwd-confirm-error" class="mt-1.5 text-xs text-red-600">
          {{ errors.confirm }}
        </p>
      </div>

      <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer w-fit">
        <input v-model="visible" type="checkbox" class="h-4 w-4 rounded border-gray-300 accent-[#0B2447]" />
        Afficher les mots de passe
      </label>

      <div class="flex flex-wrap items-center gap-4 pt-1">
        <button type="submit"
          class="rounded-xl bg-[#0B2447] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#123566] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0B2447] disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="saving">
          {{ saving ? 'Mise à jour…' : 'Mettre à jour le mot de passe' }}
        </button>
        <p v-if="status" role="status" class="text-sm"
          :class="status.type === 'success' ? 'text-emerald-700' : 'text-red-600'">
          {{ status.message }}
        </p>
      </div>
    </form>
  </section>
</template>

<script>
const STRENGTH_LEVELS = [
  { label: 'trop faible', bar: 'bg-red-400', text: 'text-red-600' },
  { label: 'faible', bar: 'bg-red-400', text: 'text-red-600' },
  { label: 'moyenne', bar: 'bg-amber-400', text: 'text-amber-700' },
  { label: 'bonne', bar: 'bg-emerald-500', text: 'text-emerald-700' },
  { label: 'excellente', bar: 'bg-emerald-600', text: 'text-emerald-700' }
]

export default {
  name: 'SecuritySettings',
  data() {
    return {
      form: { current: '', next: '', confirm: '' },
      errors: { current: null, next: null, confirm: null },
      visible: false,
      saving: false,
      status: null
    }
  },
  computed: {
    rules() {
      const p = this.form.next
      return [
        { label: '8 caractères minimum', ok: p.length >= 8, required: true },
        { label: 'Une lettre majuscule', ok: /[A-Z]/.test(p), required: true },
        { label: 'Un chiffre', ok: /\d/.test(p), required: true },
        { label: 'Un caractère spécial (recommandé)', ok: /[^A-Za-z0-9]/.test(p), required: false }
      ]
    },
    score() {
      return this.rules.filter((r) => r.ok).length
    },
    strength() {
      return STRENGTH_LEVELS[this.score]
    },
    meetsRequiredRules() {
      return this.rules.filter((r) => r.required).every((r) => r.ok)
    }
  },
  methods: {
    validate() {
      const e = { current: null, next: null, confirm: null }
      if (!this.form.current) {
        e.current = 'Saisissez votre mot de passe actuel.'
      }
      if (!this.form.next) {
        e.next = 'Saisissez un nouveau mot de passe.'
      } else if (!this.meetsRequiredRules) {
        e.next = 'Le nouveau mot de passe ne respecte pas encore toutes les conditions ci-dessous.'
      } else if (this.form.next === this.form.current) {
        e.next = 'Choisissez un mot de passe différent de l\'actuel.'
      }
      if (!this.form.confirm) {
        e.confirm = 'Confirmez le nouveau mot de passe.'
      } else if (this.form.confirm !== this.form.next) {
        e.confirm = 'Les deux mots de passe ne correspondent pas.'
      }
      this.errors = e
      return !e.current && !e.next && !e.confirm
    },
    async submit() {
      this.status = null
      if (!this.validate()) return
      this.saving = true
      try {
        await this.changePassword({
          currentPassword: this.form.current,
          newPassword: this.form.next
        })
        this.form = { current: '', next: '', confirm: '' }
        this.visible = false
        this.status = { type: 'success', message: 'Mot de passe mis à jour.' }
      } catch (err) {
        // Cas typique : mot de passe actuel refusé par le serveur
        this.status = {
          type: 'error',
          message: 'La mise à jour a échoué. Vérifiez votre mot de passe actuel et réessayez.'
        }
      } finally {
        this.saving = false
      }
    },
    // TODO : remplacer par ton vrai appel API, par exemple
    // return api.post('/me/password', payload)
    changePassword(payload) {
      return new Promise((resolve) => setTimeout(() => resolve(payload), 700))
    }
  }
}
</script>
